import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Styled components (copied from PaymentTerms.js/PrivacyPolicy.js)
const PageContainer = styled.div`
  padding-top: 0;
  min-height: 100vh;
`;

const Hero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 16rem 2rem 8rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    filter: blur(60px);
    opacity: 0.6;
  }

  h1 {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    position: relative;
    z-index: 2;
  }

  p {
    font-size: 1.25rem;
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.9;
    line-height: 1.6;
    position: relative;
    z-index: 2;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.8;
  
  h2 {
    font-size: 1.8rem;
    margin: 2rem 0 1rem;
    color: ${props => props.theme.colors.text.primary};
  }

  p, li {
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;
    list-style-type: disc;
  }

  strong {
      font-weight: bold;
  }
`;

const ResponseToLawEnforcement = () => {
  return (
    <PageContainer>
      <Hero>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Response to Law Enforcement
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Guidelines for information requests
        </motion.p>
      </Hero>

      <Content>
        <p>ServTrackr LLC ("ServTrackr"/"we"/"us") is a company based in Pompano Beach, Florida USA and we provide access to our proprietary business management platform to home service business operators and field service providers. These guidelines are intended for those seeking information about a ServTrackr account or looking to take action in respect of ServTrackr clients or resources hosted on our platform.</p>
        
        <h2>Disclosure Requests</h2>
        <p>Safeguarding our users' data is vital to the trust our users place in our service to keep their data, including the data of their customers, secure. As a U.S. company, ServTrackr's ability to disclose user information is governed by applicable U.S. privacy legislation. Accordingly, ServTrackr may disclose certain user information in response to specific types of legal process, including subpoenas, court orders, and search warrants. Any request from a non-account owner for user information must be made through a valid subpoena, court order, or search warrant (see further below).</p>
        <p>To request information regarding jobs, invoices, or payments facilitated through ServTrackr, the applicable ServTrackr customer's name and a date range must be specifically included in the request. ServTrackr cannot process overly broad or vague requests and we cannot guarantee that we will have any given sets of information for any particular user. This in part depends on our data retention policies which vary according to the type of information and actions of users.</p>
        <p>ServTrackr shall use commercially reasonable efforts to provide all information validly requested in accordance with this policy. ServTrackr reserves the right to charge its users, on a time and materials basis, for ServTrackr's efforts taken to respond to valid law enforcement requests initiated at the User's request.</p>
        
        <h2>Requests from Government Agencies/Law Enforcement</h2>
        <p>Except in rare exigent circumstances, ServTrackr turns over user information only upon receipt of a valid court order, subpoena, or search warrant issued by a U.S. authority. Additionally, we may notify affected users about any requests for their account information, unless prohibited from doing so by law or court order (see more below).</p>
        <p>For legal requests from government agencies/law enforcement outside of the United States, we require that the request be served via a U.S. court (e.g., under the procedures of an applicable mutual legal assistance treaty (MLAT)).</p>
        
        <h2>Notification to ServTrackr Users</h2>
        <p>If a request for information is valid, we will preserve the necessary information, and then make a reasonable effort to notify any affected account owner(s) by sending a message to their verified email address. We will also notify users of any legal process regarding their account unless we are prohibited by law or court order from doing so. Notwithstanding the foregoing, we may choose not to notify a user if we deem, in our sole discretion, that providing notice would be ineffective, prejudicial, might create a risk of injury or bodily harm to an individual or group, or to our property, or is otherwise inappropriate in the circumstances. In most cases, upon notification to the user, that user may have the right to file an objection with the court or otherwise legally challenge the request. If, prior to the deadline, we receive notice from the user that he or she has filed an objection to challenge a request, no information will be delivered until that process concludes.</p>
        
        <h2>Serving Process on ServTrackr</h2>
        <p>Any request for user information must include a valid email address for us to return the information or contact with questions. ServTrackr communicates only via email with a confirmed receipt.</p>
        <p>Where permitted, ServTrackr prefers to receive service via email to <a href="mailto:contact@servtrackr.com">contact@servtrackr.com</a>. Legal process can also be served by mail to:</p>
        <p>
            ServTrackr LLC<br />
            240 S Andrews Ave STE 10<br />
            Pompano Beach, FL 33069<br />
            USA
        </p>
        <p>Please make your requests as specific and narrow as possible, including the following information:</p>
        <ul>
            <li>Full information about authority issuing the request for information</li>
            <li>The name and badge/ID of the responsible agent</li>
            <li>An official email address and contact phone number</li>
            <li>The IP address, date range, domain name(s) of interest</li>
            <li>The description of the types of records you need</li>
        </ul>
        <p>Please allow at least two weeks for us to be able to look into your request. ServTrackr reserves the right to make changes to any of the foregoing practices in its sole discretion.</p>
      </Content>
    </PageContainer>
  );
};

export default ResponseToLawEnforcement; 